import React, { useMemo } from 'react';
import uniqid from 'uniqid';
import { twMerge } from 'tailwind-merge';
import { Tooltip as ReactTooltip, type PlacesType } from 'react-tooltip';

type TooltipPosition =
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'bottom-start'
  | 'bottom'
  | 'bottom-end'
  | 'left-start'
  | 'left'
  | 'left-end'
  | 'right-start'
  | 'right'
  | 'right-end';

const PLACEMENT_MAP: Record<TooltipPosition, string> = {
  'top-start': 'top-start',
  top: 'top',
  'top-end': 'top-end',
  'bottom-start': 'bottom-start',
  bottom: 'bottom',
  'bottom-end': 'bottom-end',
  'left-start': 'left-start',
  left: 'left',
  'left-end': 'left-end',
  'right-start': 'right-start',
  right: 'right',
  'right-end': 'right-end',
};

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  enabled?: boolean;
  position?: string;
  positionStrategy?: 'absolute' | 'fixed';
  anchorStrategy?: 'wrapper' | 'child';
  customClass?: string;
}

const Tooltip = ({
  content,
  children,
  enabled = true,
  position = 'bottom',
  positionStrategy = 'absolute',
  anchorStrategy = 'wrapper',
  customClass = '',
}: TooltipProps): React.JSX.Element => {
  const tooltipId = useMemo(() => `tooltip-${uniqid()}`, []);
  const placement = (PLACEMENT_MAP[position as TooltipPosition] ||
    'bottom') as PlacesType;

  if (!enabled) {
    return <>{children}</>;
  }

  const trigger =
    anchorStrategy === 'child' && React.isValidElement(children) ? (
      React.cloneElement(
        children as React.ReactElement<Record<string, unknown>>,
        { 'data-tooltip-id': tooltipId }
      )
    ) : (
      <div data-tooltip-id={tooltipId}>{children}</div>
    );

  return (
    <>
      {trigger}
      <ReactTooltip
        id={tooltipId}
        content={content as string}
        place={placement}
        positionStrategy={positionStrategy}
        delayShow={100}
        className={twMerge(
          'z-50 !bg-gray-600 !text-white !text-sm !px-2 !py-1 !rounded !shadow-lg !max-w-[300px] !break-words [&>div]:!p-0',
          customClass
        )}
        opacity={1}
        clickable
      />
    </>
  );
};

export default Tooltip;
